import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import __ from "src/utils/Translations";

interface ISubOption {
  name: string;
  title: string;
}

interface IConfigOptionProps {
  name: string;
  title: string;
  description?: string;
  disabled?: boolean;
  disabledByParent: boolean;
  parentAbsenceType: string | null;
  dataTestId?: string;
  hasSubOption?: boolean;
  subOption?: ISubOption;
}

const ConfigOption: FC<IConfigOptionProps> = ({
  name,
  title,
  description = null,
  disabled = false,
  disabledByParent,
  parentAbsenceType,
  dataTestId,
  hasSubOption = false,
  subOption,
}) => {
  const { control, setValue } = useFormContext();
  const disabledOption = disabled || (!!parentAbsenceType && disabledByParent);

  const mainValue = useWatch({ control, name });

  useEffect(() => {
    if (!mainValue && hasSubOption && subOption) {
      setValue(subOption.name, false);
    }
  }, [mainValue, hasSubOption, subOption, setValue]);

  return (
    <Box
      sx={{
        bgcolor: "grey.100",
        padding: "1rem",
        marginBottom: "1rem",
        borderRadius: "0.5rem",
      }}
    >
      <Controller
        name={name}
        control={control}
        defaultValue={false}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!value}
                onChange={onChange}
                disabled={disabledOption}
                data-testid={dataTestId}
              />
            }
            label={title}
            slotProps={{
              typography: {
                variant: "subtitle1",
              },
            }}
          />
        )}
      />
      {description && (
        <Typography variant="body2" color="textSecondary">
          {__(description)}
        </Typography>
      )}
      {hasSubOption && subOption && !!mainValue && (
        <Box>
          <Controller
            name={subOption.name}
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!value}
                    onChange={onChange}
                    disabled={disabledOption}
                    data-testid={
                      dataTestId ? `${dataTestId}__required` : undefined
                    }
                  />
                }
                label={__(subOption.title)}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default ConfigOption;
